import React from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import s from './DestinationPicker.scss';

type DestinationPickerProps = {
  dataHook: string;
};

export enum DestinationPickerDataHook {
  Button = 'DestinationPicker.Button',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const DestinationPicker = ({dataHook}: DestinationPickerProps) => {
  const {t} = useTranslation();
  const {hasShippingDestination, handleChangeRegion, shouldShowShipping, shouldShowTax, shouldHideCountryPicker} =
    useControllerProps().orderStore;
  const {cartModel, refactorOrderSummaryInCartPage, isNonShippableCart} = useControllerProps().cartStore;

  const getChooseRegionTranslationKey = (): string => {
    if (shouldShowShipping && shouldShowTax) {
      return 'cart.shipping_destination.shipping_and_tax.choose_region';
    }
    if (shouldShowShipping) {
      return 'cart.shipping_destination.choose_region';
    }
    return 'cart.shipping_destination.tax.choose_region';
  };

  /**
   * @deprecated
   */
  const getRegionOld = (): string => {
    return getRegion() ?? t('cart.fallback_estimated_shipping_address');
  };

  const getRegion = (): string => {
    const {deliveryInfo, paymentInfo} = cartModel;
    const address = isNonShippableCart ? paymentInfo?.address : deliveryInfo?.address;

    if (address?.countryFullname && address?.subdivisionFullname) {
      return t('cart.estimated_shipping_address', {
        country: address.countryFullname,
        subdivision: address.subdivisionFullname,
      });
    }
    if (address?.countryFullname) {
      return address.countryFullname;
    }
  };

  const getAriaLabel = () => {
    if (refactorOrderSummaryInCartPage) {
      const region = getRegion();
      return region ? t('cart.sr_change_destination', {destinationName: region}) : t(getChooseRegionTranslationKey());
    }

    if (hasShippingDestination) {
      return t('cart.sr_change_destination', {destinationName: getRegionOld()});
    }
    return t(getChooseRegionTranslationKey());
  };

  function getButtonText() {
    if (refactorOrderSummaryInCartPage) {
      const region = getRegion();
      return region ?? t(getChooseRegionTranslationKey());
    }

    if (hasShippingDestination) {
      return getRegionOld();
    }
    return t(getChooseRegionTranslationKey());
  }

  return shouldHideCountryPicker ? null : (
    <div className={s.location} data-hook={dataHook}>
      <button
        data-hook={DestinationPickerDataHook.Button}
        type="button"
        onClick={() => handleChangeRegion()}
        aria-haspopup={true}
        aria-describedby="change-region-description"
        aria-label={getAriaLabel()}>
        {getButtonText()}
      </button>
    </div>
  );
};
